import React, { useState, useEffect, useContext } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { AuthContext } from '../../App';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useDateFormat } from '../../contexts/DateFormatContext';
import { useOddsFormat } from '../../contexts/OddsFormatContext';
import { Lock, Unlock, TrendingUp, Calendar, Target, DollarSign, CheckCircle, XCircle, Clock, Trash2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Price from '../Price';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TipsTable = ({ serviceId, isOwnService }) => {
  const { user } = useContext(AuthContext);
  const { formatDate } = useDateFormat();
  const { convertOdds } = useOddsFormat();
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTip, setSelectedTip] = useState(null);
  const [platformFee, setPlatformFee] = useState(20);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentTip, setSelectedPaymentTip] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [paymentSettings, setPaymentSettings] = useState(null);

  useEffect(() => {
    fetchTips();
    fetchPlatformFee();
    fetchPaymentSettings();
  }, [serviceId]);
  
  const fetchPaymentSettings = async () => {
    try {
      const response = await axios.get(`${API}/payment-settings-public`);
      setPaymentSettings(response.data);
      if (response.data.stripe?.enabled) {
        setPaymentMethod('stripe');
      } else if (response.data.paypal?.enabled) {
        setPaymentMethod('paypal');
      }
    } catch (error) {
      console.error('Error fetching payment settings:', error);
    }
  };

  const fetchTips = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/tipster-services/${serviceId}/tips`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setTips(response.data);
    } catch (error) {
      console.error('Error fetching tips:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformFee = async () => {
    try {
      const response = await axios.get(`${API}/payment-settings/platform-fee`);
      setPlatformFee(response.data.fee_percentage);
    } catch (error) {
      console.error('Error fetching fee:', error);
    }
  };

  const handlePurchase = async (tip) => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      toast.error('Please login to purchase tips');
      return;
    }
    
    if (tip.price === 0) {
      // Free tip - just grant access
      fetchTips();
      return;
    }

    // Paid tip - show payment modal
    setSelectedPaymentTip(tip);
    setShowPaymentModal(true);
  };

  const confirmPayment = async () => {
    if (!selectedPaymentTip) return;
    
    const token = localStorage.getItem('session_token');
    const originUrl = window.location.origin;
    
    setShowPaymentModal(false);
    toast.info('Redirecting to payment...');
    
    try {
      const endpoint = paymentMethod === 'stripe' 
        ? `/payments/tip/stripe/checkout?tip_id=${selectedPaymentTip.id}&origin_url=${encodeURIComponent(originUrl)}`
        : `/payments/tip/paypal/checkout?tip_id=${selectedPaymentTip.id}&origin_url=${encodeURIComponent(originUrl)}`;
      
      const response = await axios.post(`${API}${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Redirect to payment gateway (Stripe uses 'url', PayPal uses 'approval_url')
      const redirectUrl = response.data.url || response.data.approval_url;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        toast.error('Payment URL not received');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || 'Failed to start payment');
    }
  };

  const handleUpdateStatus = async (tipId, newStatus) => {
    const token = localStorage.getItem('session_token');
    try {
      await axios.put(`${API}/tips/${tipId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tip status updated');
      fetchTips();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteTip = async (tipId) => {
    if (!window.confirm('Are you sure you want to delete this tip? This action cannot be undone.')) {
      return;
    }

    const token = localStorage.getItem('session_token');
    try {
      await axios.delete(`${API}/tips/${tipId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tip deleted successfully');
      fetchTips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tip');
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'won': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'lost': return <XCircle className="w-5 h-5 text-red-400" />;
      case 'void': return <XCircle className="w-5 h-5 text-gray-400" />;
      default: return <Clock className="w-5 h-5 text-yellow-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'won': return 'bg-green-900/30 text-green-400 border-green-500/30';
      case 'lost': return 'bg-red-900/30 text-red-400 border-red-500/30';
      case 'void': return 'bg-gray-900/30 text-gray-400 border-gray-500/30';
      default: return 'bg-yellow-900/30 text-yellow-400 border-yellow-500/30';
    }
  };

  if (loading) {
    return <div className="text-white text-center py-8">Loading tips...</div>;
  }

  if (tips.length === 0) {
    return (
      <Card className="glass-dark border-white/10">
        <CardContent className="py-12 text-center">
          <TrendingUp className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No tips available yet</p>
          {isOwnService && (
            <p className="text-gray-500 text-sm mt-2">Create your first tip above</p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <CardHeader className="px-0">
        <CardTitle className="text-white">Latest Tips</CardTitle>
      </CardHeader>

      {tips.map((tip) => {
        const tipDate = new Date(tip.tip_date);
        const isPast = tipDate < new Date();
        const netEarning = tip.price > 0 ? (tip.price * (100 - platformFee) / 100).toFixed(2) : 0;

        return (
          <Card key={tip.id} className="glass-dark border-white/10 hover:border-blue-500/30 transition-all">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row justify-between gap-4">
                
                {/* Left: Tip Info */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        {getStatusIcon(tip.status)}
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(tip.status)}`}>
                          {tip.status.toUpperCase()}
                        </span>
                        {tip.is_parlay && (
                          <span className="px-3 py-1 rounded-full text-xs bg-purple-900/30 text-purple-400 border border-purple-500/30">
                            PARLAY
                          </span>
                        )}
                        {tip.price === 0 && (
                          <span className="px-3 py-1 rounded-full text-xs bg-green-900/30 text-green-400 border border-green-500/30">
                            FREE
                          </span>
                        )}
                      </div>
                      <h3 className="text-white text-lg font-semibold">
                        {tip.matches.length === 1 ? 
                          `${tip.matches[0].home_team} vs ${tip.matches[0].away_team}` :
                          `${tip.matches.length} Match Parlay`
                        }
                      </h3>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                    <div>
                      <p className="text-gray-400 text-xs">Created</p>
                      <p className="text-white flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(tip.created_at)}
                      </p>
                      <p className="text-gray-400 text-xs flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" />
                        {new Date(tip.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Total Odds</p>
                      <p className="text-white text-lg font-bold">{convertOdds(tip.total_odds)}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Stake</p>
                      <p className="text-white font-semibold">
                        {tip.stake_recommendation} Units
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Potential Profit</p>
                      <p className="text-green-400 font-semibold">
                        +{tip.potential_profit} Units
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs">Confidence</p>
                      <p className="text-white flex items-center gap-1">
                        <Target className="w-4 h-4" />
                        {tip.confidence_level}
                      </p>
                    </div>
                  </div>

                  {/* Matches Preview or Full Details */}
                  {tip.has_access ? (
                    <div className="space-y-2">
                      {tip.matches.map((match, idx) => (
                        <div key={idx} className="bg-white/5 rounded p-3">
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="text-white font-semibold">
                                {match.home_team} vs {match.away_team}
                              </p>
                              <p className="text-gray-400 text-sm">{match.league}</p>
                              {match.match_date && (
                                <p className="text-blue-400 text-xs flex items-center gap-1 mt-1">
                                  <Clock className="w-3 h-3" />
                                  Kick-off: {formatDate(match.match_date)} {new Date(match.match_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-white font-bold">@ {convertOdds(match.odds)}</p>
                              <p className="text-gray-400 text-sm">{match.market}</p>
                            </div>
                          </div>
                          {match.prediction && (
                            <p className="text-blue-400 text-sm mt-2">
                              Prediction: {match.prediction}
                            </p>
                          )}
                        </div>
                      ))}
                      <div className="bg-blue-900/20 rounded p-3 mt-3">
                        <p className="text-gray-400 text-xs mb-1">Analysis:</p>
                        <p className="text-white text-sm">{tip.analysis}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white/5 rounded p-4 text-center">
                      <Lock className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                      <p className="text-gray-400">
                        {tip.matches.length} match{tip.matches.length > 1 ? 'es' : ''} • 
                        Purchase to view full details and analysis
                      </p>
                    </div>
                  )}
                </div>

                {/* Right: Actions */}
                <div className="flex flex-col justify-between items-end gap-4 min-w-[180px]">
                  <div className="text-right">
                    {tip.price > 0 ? (
                      <>
                        <p className="text-gray-400 text-sm">Price</p>
                        <p className="text-3xl font-bold text-blue-400 flex items-center gap-1">
                          <DollarSign className="w-6 h-6" />
                          <Price amount={tip.price} />
                        </p>
                        {isOwnService && (
                          <p className="text-gray-400 text-xs mt-1">
                            You earn: <Price amount={netEarning} /> ({100-platformFee}%)
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="text-green-400 font-bold text-xl">FREE</div>
                    )}
                  </div>

                  {!tip.has_access && !isOwnService && (
                    <Button
                      onClick={() => handlePurchase(tip)}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      <Unlock className="w-4 h-4 mr-2" />
                      {tip.price > 0 ? (
                        <>Buy for <Price amount={tip.price} /></>
                      ) : 'View Free Tip'}
                    </Button>
                  )}

                  {tip.has_access && !isOwnService && (
                    <div className="text-green-400 text-sm flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Access Granted
                    </div>
                  )}

                  {isOwnService && tip.status === 'pending' && (
                    <div className="flex flex-col gap-2 w-full">
                      <Button
                        size="sm"
                        onClick={() => handleUpdateStatus(tip.id, 'won')}
                        className="bg-green-600 hover:bg-green-700 w-full"
                      >
                        Mark Won
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleUpdateStatus(tip.id, 'lost')}
                        className="bg-red-600 hover:bg-red-700 w-full"
                      >
                        Mark Lost
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleUpdateStatus(tip.id, 'void')}
                        className="bg-gray-600 hover:bg-gray-700 w-full"
                      >
                        Mark Void
                      </Button>
                    </div>
                  )}

                  {/* Admin Only: Delete Button */}
                  {user?.is_admin && (
                    <Button
                      size="sm"
                      onClick={() => handleDeleteTip(tip.id)}
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700 w-full mt-2"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Tip
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Info about platform fee for tipsters */}
      {isOwnService && (
        <Card className="glass-dark border-yellow-500/30 bg-yellow-900/10">
          <CardContent className="p-4">
            <p className="text-yellow-400 text-sm">
              <strong>Platform Fee:</strong> {platformFee}% fee applies to all tip sales and subscriptions. 
              You keep {100-platformFee}% of each sale.
            </p>
          </CardContent>
        </Card>
      )}
      
      {/* Payment Method Selection Modal */}
      {showPaymentModal && selectedPaymentTip && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="glass-dark border-white/10 max-w-md w-full">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Purchase Tip
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowPaymentModal(false)}
                  className="text-white hover:bg-white/10"
                >
                  ✕
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-white/5 rounded p-4">
                <p className="text-gray-400 text-sm">Tip Price</p>
                <p className="text-3xl font-bold text-blue-400">
                  <Price amount={selectedPaymentTip.price} />
                </p>
              </div>
              
              <div>
                <p className="text-white font-semibold mb-3">Select Payment Method:</p>
                <div className="space-y-2">
                  {paymentSettings?.stripe?.enabled && (
                    <button
                      onClick={() => setPaymentMethod('stripe')}
                      className={`w-full p-4 rounded-lg border-2 transition-all ${
                        paymentMethod === 'stripe'
                          ? 'border-blue-500 bg-blue-500/20'
                          : 'border-white/10 bg-white/5 hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          paymentMethod === 'stripe' ? 'border-blue-500' : 'border-white/30'
                        }`}>
                          {paymentMethod === 'stripe' && (
                            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          )}
                        </div>
                        <span className="text-white font-medium">Credit/Debit Card (Stripe)</span>
                      </div>
                    </button>
                  )}
                  
                  {paymentSettings?.paypal?.enabled && (
                    <button
                      onClick={() => setPaymentMethod('paypal')}
                      className={`w-full p-4 rounded-lg border-2 transition-all ${
                        paymentMethod === 'paypal'
                          ? 'border-blue-500 bg-blue-500/20'
                          : 'border-white/10 bg-white/5 hover:border-white/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          paymentMethod === 'paypal' ? 'border-blue-500' : 'border-white/30'
                        }`}>
                          {paymentMethod === 'paypal' && (
                            <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          )}
                        </div>
                        <span className="text-white font-medium">PayPal</span>
                      </div>
                    </button>
                  )}
                </div>
              </div>
              
              <Button
                onClick={confirmPayment}
                className="bg-blue-600 hover:bg-blue-700 w-full text-lg py-6"
              >
                Proceed to Payment
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TipsTable;
